// MachineFloor — Machine Monitor (Phase 0), embeddable shop-floor view.
//
// Rendered INLINE inside the Manufacturing StagePage as a drawer (not a route).
// It draws the locked v20 floor plan inside a fixed-max-width container so the
// machine bays never stretch with the window. Clicking a machine opens the
// chairman lightbox.
//
// Live data: on localhost over the LAN it polls http://<ip>:5000/current every
// 30 s and overlays ME401018's real RPM / efficiency. In production the direct
// poll is skipped (mixed-content); real data arrives via the Phase 1 collector
// with no UI change.
//
// Temporary dev affordance: the "Use demo data" toggle. When OFF, every machine
// falls back to its real polled state only (no made-up status / efficiency), so
// we can see exactly what is genuinely coming off the machines vs. seeded demo.

import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { AuthContext } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LIVE_MACHINE_NO = "ME401018";
const LIVE_MACHINE_IP = "192.168.1.42";
const ON_LAN = typeof window !== "undefined"
  && window.location
  && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
  && window.location.protocol === "http:";

const GREY = "#3a4049", ORANGE = "#E2622B", YEL = "#E4B400";
const LED = { r: "#10b981", i: "#f59e0b", o: "#9aa4b2", x: "#cbd3dc" };

// ════════════════════════════════════════════════════════════════════════════
// FLOOR RENDERING — compact flex layout. Bands of machines sit on a light
// concrete slab; ecotile walkways (yellow ramp → orange → grey → orange → ramp)
// run between them. Each machine carries an operator pad FUSED to its aisle-
// facing edge (top or bottom), per the "each row faces its nearest aisle" rule.
// ════════════════════════════════════════════════════════════════════════════

const MH = 78; // machine row height (px) — taller so the floor breathes
const TILE = "repeating-linear-gradient(90deg,rgba(255,255,255,.06) 0 1px,transparent 1px 20px)";
const TILEV = "repeating-linear-gradient(0deg,rgba(255,255,255,.06) 0 1px,transparent 1px 20px)";

// resolve a machine's effective status/eff given the demo-data toggle + live poll
function resolveState(m, live, useDemo) {
  const isLive = m.machine_no === LIVE_MACHINE_NO;
  if (isLive && live) return { status: live.status, eff: live.eff, source: "live" };
  if (useDemo) return { status: m.demo_status, eff: m.demo_eff, source: "demo" };
  return { status: "x", eff: 0, source: "none" };
}

// brand accent by make — wordmark + a thin accent stripe in the brand colour.
// (Mazak orange #FF5901 verified from the supplied logo; Doosan blue #003DA5.)
// c = chip text colour · block (optional) = body-block tint if it differs from c
const BRAND = {
  MAZAK:    { name: "MAZAK",    c: "#FF5901" },
  // Doosan/Puma → flat sky-blue cabinet panel (matches the real machine)
  DOOSAN:   { name: "DN",       c: "#0046C0", block: "#92B7E6", flat: true },
  // Hwacheon Hi-TECH → dark charcoal body with a navy-blue base accent (real look)
  HWACHEON: { name: "HWACHEON", c: "#1B458F", block: "#2c3340", base: "#1B458F" },
  NISSIN:   { name: "NISSIN",   c: "#E60012", block: "#c2cad4", flat: true },
  DMG:      { name: "DMG MORI", c: "#1c1c1a", block: "#2aa198" }, // black wordmark, sea-green body
};
// a soft brand-tinted border, or the neutral border for unbranded makes
function brandBorder(make) {
  const bd = BRAND[(make || "").toUpperCase()];
  return bd ? `${bd.block || bd.c}66` : "#cdd5de";
}
// the dark body block, tinted toward the brand colour (a deep brand shade
// blended over the neutral #39414d). Unbranded → neutral dark.
const BLOCK_NEUTRAL = "#39414d";
function blockColor(make) {
  const bd = BRAND[(make || "").toUpperCase()];
  if (!bd) return `linear-gradient(180deg, #4a525f, ${BLOCK_NEUTRAL})`;
  const tint = bd.block || bd.c; // body tint can differ from the chip text colour
  if (bd.flat) return tint; // flat solid fill (no gradient)
  // bottom stop: brand `base` colour if set (e.g. Hwacheon charcoal → navy),
  // otherwise fade into the neutral dark for the standard 3D depth look
  const bottom = bd.base || BLOCK_NEUTRAL;
  return `linear-gradient(180deg, ${tint} 0%, ${tint}cc 45%, ${bottom} 100%)`;
}
// brand chip — light pill with the brand code, and the machine name recessed
// beneath it (smaller, bold, duller) for a clean two-line tag.
// align: "left" (lathe) · "center" (vmc/vl) · "right" (big)
function brandMark(make, label, align = "left") {
  const bd = BRAND[(make || "").toUpperCase()];
  if (!bd) return null;
  const pos = align === "center"
    ? { left: "50%", transform: "translateX(-50%)", alignItems: "center", textAlign: "center" }
    : align === "right"
    ? { right: 4, alignItems: "flex-end", textAlign: "right" }
    : { left: 4, alignItems: "flex-start", textAlign: "left" };
  return (
    <span style={{ position: "absolute", top: 5, display: "inline-flex", flexDirection: "column", background: "linear-gradient(180deg, rgba(255,255,255,.8) 0%, rgba(255,255,255,.8) 50%, rgba(255,255,255,.04) 100%)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", padding: "2px 6px", borderRadius: 4, zIndex: 4, fontFamily: "var(--font-sans, sans-serif)", border: "0.5px solid rgba(255,255,255,.5)", boxShadow: "0 1px 2px rgba(0,0,0,.12)", maxWidth: "84%", left: pos.left, right: pos.right, transform: pos.transform, alignItems: pos.alignItems }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".04em", color: bd.c, lineHeight: 1.15, textAlign: pos.textAlign }}>{bd.name}</span>
      <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: ".01em", color: "#5f6b7a", lineHeight: 1.15, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", textAlign: pos.textAlign }}>{label}</span>
    </span>
  );
}

// machine silhouette inner detail by kind. The dark body block carries the
// brand tint; the brand chip (with machine name) sits top-left.
function glyphInner(kind, make, label) {
  const brand = brandMark(make, label, "center");
  const block = blockColor(make);
  if (kind === "vmc") return (<>
    <i className="rl-mc-block" style={{ position: "absolute", left: "18%", right: "18%", top: "18%", height: "44%", background: block, borderRadius: 3 }} />
    <i style={{ position: "absolute", left: "18%", right: "42%", top: 6, height: 5, background: "#cbd3dc", borderRadius: 2 }} />
    <i style={{ position: "absolute", right: -4, top: "26%", width: 8, height: "40%", background: "#aeb6c0", borderRadius: 2 }} />
    {brand}
  </>);
  if (kind === "big") return (<>
    <i style={{ position: "absolute", left: "9%", top: "16%", width: 26, height: 26, border: "3px solid #aeb6c0", borderRadius: "50%" }} />
    <i className="rl-mc-block" style={{ position: "absolute", right: "12%", top: "16%", bottom: "28%", width: "24%", background: block, borderRadius: 3 }} />
    <i style={{ position: "absolute", right: -4, top: "28%", width: 8, height: "36%", background: "#aeb6c0", borderRadius: 2 }} />
    {brand}
  </>);
  if (kind === "vl") return (<>
    <i style={{ position: "absolute", left: "50%", top: "44%", transform: "translate(-50%,-50%)", width: 24, height: 24, border: "3px solid #39414d", borderRadius: "50%" }} />
    {brand}
  </>);
  return (<>
    <i className="rl-mc-block" style={{ position: "absolute", left: 3, top: 3, bottom: 3, width: "26%", background: block, borderRadius: 3 }} />
    <i style={{ position: "absolute", left: "32%", right: "14%", top: "20%", height: "52%", background: "repeating-linear-gradient(90deg,#d2dae3 0 5px,#bcc6d0 5px 6px)", borderRadius: 2 }} />
    <i style={{ position: "absolute", left: "32%", right: "14%", top: 5, height: 5, background: "#cbd3dc", borderRadius: 2 }} />
    <i style={{ position: "absolute", right: 4, bottom: 4, width: 11, height: 8, background: "#525c69", borderRadius: 2 }} />
    {brand}
  </>);
}

// operator pad — narrow (2 ecotiles wide): a BLACK anti-fatigue tile against
// the machine front (1 tile tall) + an orange walkway tile behind it toward
// the aisle, matching the real floor. The negative margin pulls the orange end
// over the band's 6px gap so it merges into the AisleH.
const BLACK_TILE = "#2b2f36";
// faint 500mm tile borders: a vertical seam down the middle (2 tiles wide) drawn
// over each segment's solid colour.
const PAD_SEAM = "linear-gradient(90deg, transparent calc(50% - 0.5px), rgba(255,255,255,.18) calc(50% - 0.5px), rgba(255,255,255,.18) calc(50% + 0.5px), transparent calc(50% + 0.5px))";
function Pad({ side }) {
  const black = <i style={{ display: "block", height: 9, background: BLACK_TILE, backgroundImage: PAD_SEAM }} />;
  const orange = <i style={{ display: "block", height: 9, background: ORANGE, backgroundImage: PAD_SEAM }} />;
  const pull = side === "top" ? { marginTop: -6 } : { marginBottom: -6 };
  // machine-facing edge = black; aisle-facing edge = orange; thin line between them
  const divider = <i style={{ display: "block", height: 1, background: "rgba(255,255,255,.18)" }} />;
  return (
    <div style={{ display: "flex", justifyContent: "center", position: "relative", zIndex: 1, ...pull }} aria-hidden="true">
      <div style={{ width: 18, maxWidth: "30%" }}>
        {side === "top" ? <>{orange}{divider}{black}</> : <>{black}{divider}{orange}</>}
      </div>
    </div>
  );
}

// andon tower light — a small CNC stack lamp (red/amber/green) where the
// segment matching the machine's state is lit and the others sit dim.
// running → green · idle → amber · off → red · unknown → all dim.
const TOWER = { red: "#e2433b", amber: "#f5a623", green: "#27c08a" };
function TowerLight({ status }) {
  const lit = status === "R" || status === "r" ? "green" : status === "i" ? "amber" : status === "o" ? "red" : null;
  const seg = (key) => {
    const on = key === lit;
    return (
      <i className={on ? "rl-mc-led-on" : undefined} style={{
        display: "block", height: 5, borderRadius: 1,
        background: on ? TOWER[key] : `${TOWER[key]}33`,
        boxShadow: on ? `0 0 4px ${TOWER[key]}, inset 0 0 1px rgba(255,255,255,.6)` : "none",
      }} />
    );
  };
  // sits ABOVE the machine top edge (protruding like a real andon stack), with a
  // short stem dropping onto the machine. Lives outside the body's clipped box.
  return (
    <div style={{ position: "absolute", top: -13, right: 8, width: 9, zIndex: 7, display: "flex", flexDirection: "column", alignItems: "center" }} aria-hidden="true">
      <div style={{ width: 9, display: "flex", flexDirection: "column", gap: 1, padding: 1.5, background: "linear-gradient(180deg,#fafbfc,#e7ebf0)", borderRadius: "3px 3px 1px 1px", boxShadow: "0 1px 3px rgba(0,0,0,.3)", border: "0.5px solid rgba(0,0,0,.12)" }}>
        {seg("red")}{seg("amber")}{seg("green")}
      </div>
      {/* stem onto the machine roof */}
      <i style={{ width: 2.5, height: 5, background: "#8a94a3", borderRadius: "0 0 1px 1px" }} />
    </div>
  );
}

// Maintenance wrench (Phase 2) — a machine with an open breakdown carries a
// small corner badge, read from /machines/registry's additive
// maintenance_status. Nothing renders when the machine is fine, so the floor is
// pixel-identical to before on a plant with no open tickets.
const WRENCH = {
  down:            { color: "#e2433b", title: "Down — reported to Maintenance" },
  in_repair:       { color: "#f5a623", title: "Under repair — maintenance is on it" },
  awaiting_verify: { color: "#14b8a6", title: "Repaired — waiting for production's sign-off" },
};
function MaintenanceBadge({ status }) {
  const w = WRENCH[status];
  if (!w) return null;
  return (
    <div
      title={w.title}
      data-testid={`machine-wrench-${status}`}
      style={{
        position: "absolute", top: -5, left: -5, width: 14, height: 14, zIndex: 8,
        display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: "50%", background: "#fff", border: `1px solid ${w.color}`,
        boxShadow: "0 1px 2px rgba(40,52,72,.2)", pointerEvents: "none",
      }}
    >
      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={w.color}
           strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14.7 6.3a4 4 0 0 0 5 5l-9.4 9.4a2.1 2.1 0 0 1-3-3z" />
      </svg>
    </div>
  );
}

// a clickable machine tile with its fused operator pad
function Tile({ m, pad, live, useDemo, onClick }) {
  const isLive = m.machine_no === LIVE_MACHINE_NO;
  const { status, eff, source } = resolveState(m, live, useDemo);
  const hero = status === "R" || (isLive && source === "live");
  const color = LED[hero ? "r" : status] || LED.x;
  const barW = (status === "o" || status === "x") ? 0 : eff;
  const title = source === "none" ? "no live data" : (status === "o" ? "off" : `${eff}% today`);
  // branded machines carry their name inside the chip, so skip the external label
  const branded = !!BRAND[(m.make || "").toUpperCase()];
  const extLabel = (mt) => branded ? null : (
    <div style={{ fontSize: 10, color: "#414a57", lineHeight: 1.1, [mt ? "marginTop" : "marginBottom"]: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "center" }}>{m.label}</div>
  );
  return (
    <div
      title={title}
      onClick={() => onClick(m)}
      data-testid={`machine-tile-${m.machine_no}`}
      style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", position: "relative", cursor: "pointer" }}
    >
      {/* order from machine outward to the aisle = machine → pad; label on far side.
          Branded machines show their name in the chip; unbranded use the external label. */}
      {pad === "top" && <Pad side="top" />}
      {pad === "bottom" && extLabel(false)}
      {/* wrapper is relative + un-clipped so the tower can bulge above the roof */}
      <div style={{ position: "relative" }}>
        <TowerLight status={status} />
        <MaintenanceBadge status={m.maintenance_status} />
        <div style={{ position: "relative", height: MH, background: "linear-gradient(150deg,#fdfefe,#eef2f6)", border: hero ? "2px solid #10b981" : `1px solid ${brandBorder(m.make)}`, borderRadius: 6, overflow: "hidden", boxShadow: "0 1px 2px rgba(40,52,72,.07)" }}>
          {/* silhouette occupies the upper region, clear of the footer */}
          <div style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 12 }}>
            {glyphInner(m.kind, m.make, m.label)}
          </div>
          {/* dedicated footer track — visually separated from the machine block */}
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 11, background: "#eef1f5", borderTop: "1px solid rgba(40,52,72,.1)", display: "flex", alignItems: "center", padding: "0 4px" }}>
            <i style={{ flex: 1, height: 4, borderRadius: 3, background: "rgba(40,52,72,.14)", overflow: "hidden" }}>
              <i style={{ display: "block", height: "100%", width: `${barW}%`, borderRadius: 3, background: color }} />
            </i>
          </div>
        </div>
      </div>
      {pad === "top" && extLabel(true)}
      {pad === "bottom" && <Pad side="bottom" />}
      {isLive && (
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", top: pad === "top" ? -10 : -22, background: "rgba(255,255,255,.92)", backdropFilter: "blur(8px)", border: "1px solid rgba(16,185,129,.5)", borderRadius: 8, padding: "1px 7px", fontSize: 10, color: "#0b7a5c", whiteSpace: "nowrap", zIndex: 6, boxShadow: "0 4px 10px rgba(16,185,129,.18)" }}>
          {m.machine_no}{live ? ` · ${live.rpm} rpm · ${live.eff}%` : (ON_LAN ? " · polling…" : " · live")}
        </div>
      )}
    </div>
  );
}

// a band of machines on the slab; pad = aisle-facing side ("top"|"bottom")
function Bay({ items, pad, live, useDemo, onClick }) {
  return (
    <div style={{ flex: 1, display: "flex", gap: 7, minWidth: 0, alignItems: pad === "top" ? "flex-start" : "flex-end" }}>
      {items.map((m) => <Tile key={m.machine_no} m={m} pad={pad} live={live} useDemo={useDemo} onClick={onClick} />)}
    </div>
  );
}

// ── walkway primitives ───────────────────────────────────────────────────────
// Horizontal aisle bleeds left/right into the vertical channels (negative margin
// + matching grey) so the grey ecotile path is continuous through every T —
// no seam at the junction. A small yellow zebra marks each channel crossing.
const CHAN_BLEED = 26; // px each side — must equal the channel flex-basis
const AisleH = () => (
  <div style={{ flex: "none", margin: "6px 0", marginLeft: -CHAN_BLEED, marginRight: -CHAN_BLEED, position: "relative", zIndex: 2 }}>
    <i style={{ display: "block", height: 3, background: YEL, margin: `0 ${CHAN_BLEED}px` }} />
    <div style={{ background: ORANGE, padding: "2px 0", margin: `0 ${CHAN_BLEED}px` }}>
      <i style={{ display: "block", height: 16, background: GREY, backgroundImage: TILE }} />
    </div>
    <i style={{ display: "block", height: 3, background: YEL, margin: `0 ${CHAN_BLEED}px` }} />
    {/* grey continues straight through both channel crossings (seamless T) */}
    <i style={{ position: "absolute", left: 0, top: 3, width: CHAN_BLEED, bottom: 3, background: GREY, backgroundImage: TILE }} />
    <i style={{ position: "absolute", right: 0, top: 3, width: CHAN_BLEED, bottom: 3, background: GREY, backgroundImage: TILE }} />
    {/* yellow zebra at the two crossing mouths */}
    <i style={{ position: "absolute", left: CHAN_BLEED - 8, top: 3, width: 8, bottom: 3, background: "repeating-linear-gradient(0deg,#E4B400 0 5px,transparent 5px 10px)" }} />
    <i style={{ position: "absolute", right: CHAN_BLEED - 8, top: 3, width: 8, bottom: 3, background: "repeating-linear-gradient(0deg,#E4B400 0 5px,transparent 5px 10px)" }} />
  </div>
);
// `bleedTop` pulls the divider up into the aisle above so the grey lane is
// continuous through the T (no seam where the divider meets the aisle).
const Vdiv = ({ bleedTop = 0 }) => (
  <div style={{ flex: "0 0 22px", margin: "0 6px", marginTop: -bleedTop, display: "flex", position: "relative", zIndex: 2 }}>
    <i style={{ width: 3, background: YEL, marginTop: bleedTop }} />
    <div style={{ flex: 1, background: ORANGE, padding: "0 2px" }}>
      <i style={{ display: "block", height: "100%", background: GREY, backgroundImage: TILEV }} />
    </div>
    <i style={{ width: 3, background: YEL, marginTop: bleedTop }} />
    {/* grey runs straight up through the aisle crossing (covers the orange seam) */}
    {bleedTop > 0 && <i style={{ position: "absolute", left: 5, right: 5, top: 0, height: bleedTop, background: GREY, backgroundImage: TILEV }} />}
  </div>
);
const Vchannel = () => (
  <div style={{ flex: "0 0 26px", display: "flex" }}>
    <i style={{ width: 3, background: YEL }} />
    <div style={{ flex: 1, background: ORANGE, padding: "0 3px" }}><i style={{ display: "block", height: "100%", background: GREY, backgroundImage: TILEV }} /></div>
    <i style={{ width: 3, background: YEL }} />
  </div>
);
// faint furniture figures so the support rooms read as real spaces
const FURN = "#c9d1da", FURN_B = "#b6bfca", SEAT = "#aeb6c0";
function roomFigure(kind) {
  const dk = (l, t, w, h, k) => <i key={k} style={{ position: "absolute", left: l, top: t, width: w, height: h, background: FURN, border: `1px solid ${FURN_B}`, borderRadius: 2 }} />;
  const seat = (l, t, k) => <i key={k} style={{ position: "absolute", left: l, top: t, width: 6, height: 6, borderRadius: "50%", background: SEAT }} />;
  switch (kind) {
    case "stairs": return Array.from({ length: 5 }).map((_, i) => <i key={i} style={{ position: "absolute", left: 8, right: 8, top: 6 + i * 7, height: 3, background: FURN }} />);
    case "lockers": return Array.from({ length: 5 }).map((_, i) => dk(8 + i * 18, 8, 14, "60%", i));
    case "bench": return [dk(8, 8, 14, 12, "a"), dk(26, 8, 14, 12, "b"), dk(44, 8, 14, 12, "c"), dk(8, 26, 40, 9, "d"), seat(16, 40, "s1"), seat(30, 40, "s2")];
    case "cmm": return [<i key="m" style={{ position: "absolute", left: 10, top: 10, width: "46%", height: "46%", border: `2px solid ${FURN_B}`, borderRadius: 3 }} />, <i key="d" style={{ position: "absolute", right: 10, top: 12, width: 28, height: 14, background: FURN, border: `1px solid ${FURN_B}`, borderRadius: 2 }} />, seat(38, 44, "s")];
    case "sofa": return [dk(8, 8, "70%", 10, "a"), dk(8, 24, "70%", 10, "b"), <i key="c" style={{ position: "absolute", right: 8, top: 12, width: 18, height: 18, border: `2px solid ${FURN_B}`, borderRadius: "50%" }} />];
    case "table": return [<i key="t" style={{ position: "absolute", left: "50%", top: "42%", transform: "translate(-50%,-50%)", width: "62%", height: 16, background: FURN, border: `1px solid ${FURN_B}`, borderRadius: 8 }} />, seat("24%", "14%", "s1"), seat("50%", "14%", "s2"), seat("70%", "14%", "s3"), seat("24%", "74%", "s4"), seat("50%", "74%", "s5"), seat("70%", "74%", "s6")];
    case "desk": return [dk(8, 10, "62%", 10, "a"), seat(18, 28, "s")];
    default: return null;
  }
}
const Room = ({ label, flex, kind, door }) => (
  <div style={{ flex: `${flex} 1 0`, background: "linear-gradient(160deg,#f3f6f9,#e7ecf2)", border: "1px solid #d6dce4", borderRadius: 6, position: "relative", padding: 4, minHeight: 26, boxShadow: "inset 0 1px 0 rgba(255,255,255,.6)", overflow: "visible" }}>
    {roomFigure(kind)}
    <span style={{ position: "absolute", left: 6, bottom: 3, fontSize: 10, color: "#8a94a3", zIndex: 1 }}>{label}</span>
    {/* entry to the shop floor — an elegant green ‹ ENTRY marker on the right wall */}
    {door && (
      <div style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 4, fontSize: 9, fontWeight: 500, letterSpacing: ".04em", color: "#0b8a63", zIndex: 3 }} aria-hidden="true">
        ENTRY
        <i style={{ width: 0, height: 0, borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "7px solid #1d9e75" }} />
      </div>
    )}
  </div>
);

// ── realism decals — emergency exits + machine roller door (per CAD) ─────────
function FloorDecals() {
  const exit = (style) => (
    <div style={{ position: "absolute", display: "flex", alignItems: "center", gap: 4, fontSize: 9, fontWeight: 500, letterSpacing: ".04em", color: "#b3402e", ...style }} aria-hidden="true">
      <i style={{ width: 0, height: 0, borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "7px solid #d8453a" }} />
      EXIT
    </div>
  );
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
      {/* TOP-RIGHT: rolling shutter (the big machine in/out door) */}
      <div style={{ position: "absolute", top: 0, right: 30, width: 78, height: 13, background: "repeating-linear-gradient(90deg,#cdd4dc 0 4px,#bcc4cd 4px 7px)", borderRadius: "0 0 3px 3px", border: "1px solid #b1bac4", borderTop: "none" }} aria-hidden="true" />
      <div style={{ position: "absolute", top: 15, right: 30, width: 78, textAlign: "center", fontSize: 8, fontWeight: 600, letterSpacing: ".05em", color: "#8a94a3" }} aria-hidden="true">SHUTTER</div>
      {/* emergency exit — right edge (was the roller-door spot) */}
      {exit({ top: "46%", right: 6 })}
    </div>
  );
}

// ── mobile: a simple zone-grouped list (no floor plan) ───────────────────────
const ZONES = [
  ["Turning & long-bed", ["b1L", "b1R", "b2a", "b2b"]],
  ["Multitask & turnmill", ["b3", "b4"]],
  ["Vertical & 5-axis", ["b5L", "b5R"]],
];
function MobileList({ byBand, live, useDemo, onClick }) {
  const b = (k) => byBand[k] || [];
  return (
    <div style={{ fontFamily: "var(--font-sans, sans-serif)" }}>
      <div style={{ background: "rgba(250,238,218,.6)", border: "1px solid rgba(186,117,23,.3)", borderRadius: 10, padding: "10px 12px", fontSize: 12.5, color: "#7a5410", marginBottom: 14, lineHeight: 1.4 }}>
        Open on your laptop or desktop for the full shop-floor view. This compact list shows each machine’s live status and efficiency.
      </div>
      {ZONES.map(([zone, keys]) => {
        const items = keys.flatMap(b);
        if (!items.length) return null;
        return (
          <div key={zone} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, letterSpacing: ".06em", color: "#8a94a3", textTransform: "uppercase", margin: "0 0 6px 2px" }}>{zone} · {items.length}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.map((m) => {
                const { status, eff, source } = resolveState(m, live, useDemo);
                const isLive = m.machine_no === LIVE_MACHINE_NO;
                const hero = status === "R" || (isLive && source === "live");
                const color = LED[hero ? "r" : status] || LED.x;
                const right = source === "none" ? "—" : (status === "o" ? "off" : `${eff}%`);
                return (
                  <div key={m.machine_no} onClick={() => onClick(m)} data-testid={`machine-row-${m.machine_no}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#fbfcfe", border: hero ? "1px solid rgba(16,185,129,.4)" : "1px solid #e2e7ed", borderRadius: 10, cursor: "pointer" }}>
                    <i style={{ width: 9, height: 9, borderRadius: "50%", background: color, flex: "none", boxShadow: `0 0 0 3px ${color}22` }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: "#222b38", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.label}</div>
                      <div style={{ fontSize: 11, color: "#8a94a3", fontFamily: "var(--font-mono, monospace)" }}>{m.machine_no}{isLive && live ? ` · ${live.rpm} rpm` : ""}</div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 500, color: status === "o" ? "#9aa4b2" : (status === "i" ? "#b07207" : "#0b8a63"), fontFamily: "var(--font-mono, monospace)" }}>{right}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MachineFloor() {
  const { user } = useContext(AuthContext);
  const [machines, setMachines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [live, setLive] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [useDemo, setUseDemo] = useState(true); // temporary dev toggle

  // narrow viewport → show a list instead of the floor plan
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.matchMedia("(max-width: 760px)").matches; } catch { return false; }
  });
  useEffect(() => {
    let mq; try { mq = window.matchMedia("(max-width: 760px)"); } catch { return; }
    const on = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // auth via Bearer token header — same pattern as the rest of RockLink.
        // (Do NOT use withCredentials: it forces credentialed CORS, which the
        // production cross-origin setup — custom domain → emergent.host API with
        // Access-Control-Allow-Origin: * — rejects. Token header works with *.)
        const token = localStorage.getItem("rl_token");
        const authCfg = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
        const [reg, sum] = await Promise.all([
          axios.get(`${API}/machines/registry`, authCfg),
          axios.get(`${API}/machines/summary`, authCfg),
        ]);
        if (cancelled) return;
        setMachines(reg.data.machines || []);
        setSummary(sum.data || null);
      } catch (e) {
        if (!cancelled) setError("Could not load machines.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pollLive = useCallback(async () => {
    if (!ON_LAN) return;
    try {
      const r = await axios.get(`http://${LIVE_MACHINE_IP}:5000/current`, { timeout: 4000 });
      const snap = parseMTConnect(r.data);
      if (snap) setLive(snap);
    } catch { /* unreachable — keep last known */ }
  }, []);

  useEffect(() => {
    if (!ON_LAN) return;
    pollLive();
    const id = setInterval(pollLive, 30000);
    return () => clearInterval(id);
  }, [pollLive]);

  const byBand = useMemo(() => {
    const g = {};
    for (const m of machines) (g[m.band] ||= []).push(m);
    for (const k of Object.keys(g)) g[k].sort((a, b) => a.order - b.order);
    return g;
  }, [machines]);
  const b = (k) => byBand[k] || [];

  // live-aware summary when demo is off
  const liveSummary = useMemo(() => {
    if (useDemo) return summary;
    const running = live && (live.status === "R" || live.status === "r") ? 1 : 0;
    return { total: machines.length, running, idle: 0, off: 0, plant_eff: live ? live.eff : 0, unknown: machines.length - running };
  }, [useDemo, summary, live, machines.length]);

  return (
    <div className="rl-machine-floor" style={{ fontFamily: "var(--font-sans, sans-serif)", color: "#222b38" }}>
      {/* inner glass sub-header with the demo toggle (no page chrome — the
          StagePage card already provides header/footer) */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Shop floor — {ON_LAN ? "live LAN" : "preview"}</span>
        <span style={{ fontSize: 11, color: "#8a94a3" }}>Bhateri COE · {liveSummary?.total ?? machines.length} machines</span>
        {/* delivery disclaimer — Phase 0 preview; real stats arrive with the collector */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 500, color: "#7a5410", background: "rgba(250,238,218,.7)", border: "1px solid rgba(186,117,23,.35)", padding: "3px 9px", borderRadius: 999 }}>
          <i style={{ width: 6, height: 6, borderRadius: "50%", background: "#ba7517" }} />
          Feature work in progress — machine stats coming soon
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 14, fontSize: 12, fontFamily: "var(--font-mono, monospace)", alignItems: "center" }}>
          <span style={{ color: "#0b8a63" }}>● {liveSummary?.running ?? "–"}</span>
          <span style={{ color: "#b07207" }}>● {liveSummary?.idle ?? "–"}</span>
          <span style={{ color: "#7c8694" }}>● {liveSummary?.off ?? "–"}</span>
          <span>plant eff <b style={{ fontWeight: 500 }}>{liveSummary?.plant_eff ?? "–"}%</b></span>
        </span>
        {/* TEMPORARY dev toggle — remove once the Phase 1 collector feeds real data */}
        <label title="Temporary: turn off to see only real polled data" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: useDemo ? "#8a94a3" : "#0b7a5c", cursor: "pointer", padding: "3px 8px", border: "1px dashed #c9d1da", borderRadius: 8 }}>
          <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} data-testid="machines-demo-toggle" />
          Use demo data
        </label>
      </div>

      {loading && <div style={{ color: "#5f6b7a", padding: "8px 0" }}>Loading shop floor…</div>}
      {error && <div style={{ color: "#a32d2d", padding: "8px 0" }}>{error}</div>}

      {!loading && !error && isMobile && (
        <MobileList byBand={byBand} live={live} useDemo={useDemo} onClick={setSelected} />
      )}

      {!loading && !error && !isMobile && (
        <div style={{ position: "relative", display: "flex", gap: 8, alignItems: "stretch", background: "#c4c8ce", backgroundImage: "repeating-linear-gradient(0deg,transparent 0 79px,rgba(40,52,72,.05) 79px 80px),repeating-linear-gradient(90deg,transparent 0 89px,rgba(40,52,72,.05) 89px 90px)", borderRadius: 10, padding: 10, boxShadow: "inset 0 0 0 1px rgba(255,255,255,.35)" }}>
          <FloorDecals />
          {/* left support wing */}
          <div style={{ flex: "0 0 116px", display: "flex", flexDirection: "column", gap: 5, marginRight: 4 }}>
            <Room label="Stairs" flex={32} kind="stairs" /><Room label="W/C" flex={48} /><Room label="Lockers" flex={44} kind="lockers" />
            <Room label="Tool crib" flex={90} kind="bench" /><Room label="CMM room" flex={78} kind="cmm" /><Room label="Lounge" flex={70} kind="sofa" door />
            <Room label="Meeting room" flex={84} kind="table" /><Room label="Reception" flex={78} kind="desk" />
          </div>
          <Vchannel />
          {/* machine hall on the concrete slab */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, justifyContent: "space-between" }}>
            <div style={{ display: "flex" }}>
              <Bay items={[...b("b1L"), ...b("b1R")]} pad="bottom" live={live} useDemo={useDemo} onClick={setSelected} />
            </div>
            <AisleH />
            <Bay items={b("b2a")} pad="top" live={live} useDemo={useDemo} onClick={setSelected} />
            <i style={{ height: 8, flex: "none" }} />
            <Bay items={b("b2b")} pad="bottom" live={live} useDemo={useDemo} onClick={setSelected} />
            <AisleH />
            <Bay items={b("b3")} pad="top" live={live} useDemo={useDemo} onClick={setSelected} />
            <i style={{ height: 8, flex: "none" }} />
            <Bay items={b("b4")} pad="bottom" live={live} useDemo={useDemo} onClick={setSelected} />
            <AisleH />
            <div style={{ display: "flex" }}>
              <Bay items={b("b5L")} pad="top" live={live} useDemo={useDemo} onClick={setSelected} />
              <Vdiv />
              <Bay items={b("b5R")} pad="top" live={live} useDemo={useDemo} onClick={setSelected} />
            </div>
          </div>
          <Vchannel />
          {/* right office block */}
          <div style={{ flex: "0 0 92px", display: "flex", flexDirection: "column", gap: 5, marginLeft: 4 }}>
            <i style={{ flex: 1 }} />
            {[["Conference", 48, "table"], ["Office 4", 36, "desk"], ["Office 3", 36, "desk"], ["Office 2", 36, "desk"], ["Office 1", 36, "desk"], ["Maint. room", 40, "bench"], ["Battery / UPS", 34, null], ["Panels", 26, null]].map(([n, h, kind]) => (
              <div key={n} style={{ flex: "none", height: h, background: "linear-gradient(160deg,#f3f6f9,#e7ecf2)", border: "1px solid #d6dce4", borderRadius: 6, position: "relative", overflow: "hidden" }}>
                {roomFigure(kind)}
                <span style={{ position: "absolute", left: 6, bottom: 3, fontSize: 10, color: "#8a94a3", zIndex: 1 }}>{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected && (
        <MachineLightbox
          m={selected}
          live={selected.machine_no === LIVE_MACHINE_NO ? live : null}
          useDemo={useDemo}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

const btnStyle = {
  fontSize: 13, padding: "7px 14px", borderRadius: 8, border: "1px solid #cdd5de",
  background: "rgba(255,255,255,.7)", color: "#2b3340", cursor: "pointer",
};

// ── chairman lightbox ────────────────────────────────────────────────────────
function MachineLightbox({ m, live, useDemo, onClose }) {
  const isLive = m.machine_no === LIVE_MACHINE_NO;
  const statusLabel = live ? "Running" : (useDemo ? (m.demo_status === "o" ? "Off" : m.demo_status === "i" ? "Idle" : "Running") : "No live data");
  const today = live ? live.eff : (useDemo ? m.demo_eff : "—");
  // Live detail row values (real when polled; otherwise a clear unavailable state)
  const lan = ON_LAN && isLive;
  const liveRows = [
    { k: "Spindle", v: live ? `${live.rpm} rpm` : (lan ? "polling…" : "—") },
    { k: "Mode", v: live ? (live.mode || "—") : (lan ? "polling…" : "—") },
    { k: "Program", v: live ? (live.program || "—") : (lan ? "polling…" : "—") },
    { k: "Tool", v: live ? (live.tool || "—") : (lan ? "polling…" : "—") },
    { k: "Part count", v: live ? (live.parts ?? "—") : (lan ? "polling…" : "—") },
    { k: "Efficiency today", v: live ? `${live.eff}%` : (lan ? "polling…" : "—") },
  ];
  const detailNote = isLive
    ? (live ? `Live — direct from ${m.ip}` : (ON_LAN ? `Polling ${m.ip} (no response yet)` : `Live detail available on the shop LAN; via the collector in production`))
    : (m.mtconnect ? `MTConnect-capable — live detail arrives with the Phase 1 collector` : `Not MTConnect-confirmed — live detail pending a protocol scan`);

  return (
    <div className="rl-machine-lightbox" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(34,43,56,.42)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 26, zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 640, background: "rgba(255,255,255,.92)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,.9)", borderRadius: 16, boxShadow: "0 20px 60px rgba(20,28,40,.30)", overflow: "hidden", fontFamily: "var(--font-sans, sans-serif)", color: "#222b38" }}>
        <div style={{ padding: "16px 18px", display: "flex", alignItems: "flex-start", gap: 14, borderBottom: "1px solid rgba(140,150,165,.18)" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 17, fontWeight: 500, color: "#1f2733" }}>{m.label}</span>
              <span style={{ fontSize: 11, fontFamily: "var(--font-mono, monospace)", color: "#5f6b7a", background: "rgba(140,150,165,.14)", padding: "1px 7px", borderRadius: 5 }}>{m.machine_no}</span>
            </div>
            <div style={{ fontSize: 12, color: "#8a94a3", marginTop: 2 }}>{m.make} · {m.model} · {m.ip}</div>
          </div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: live ? "#0b7a5c" : "#5f6b7a", background: live ? "rgba(16,185,129,.13)" : "rgba(140,150,165,.12)", border: `1px solid ${live ? "rgba(16,185,129,.32)" : "rgba(140,150,165,.3)"}`, padding: "4px 10px", borderRadius: 8 }}>
            <i style={{ width: 7, height: 7, borderRadius: "50%", background: live ? "#10b981" : "#9aa4b2", display: "inline-block" }} />{statusLabel}
          </span>
          <span onClick={onClose} style={{ fontSize: 18, color: "#9aa4b2", cursor: "pointer", lineHeight: 1 }}>✕</span>
        </div>

        <div style={{ padding: "16px 18px 6px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          <KPI label="Today" value={typeof today === "number" ? `${today}%` : today} tone="green" sub="efficiency" />
          <KPI label="This week" value={useDemo ? "68%" : "—"} tone="green" sub={useDemo ? "▲ +4 vs last wk" : "needs history"} />
          <KPI label="This month" value={useDemo ? "61%" : "—"} tone="amber" sub={useDemo ? "below 70% target" : "needs history"} />
        </div>

        {/* LIVE MACHINE DETAIL — always rendered below the hero stats */}
        <div style={{ padding: "8px 18px 4px" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#5f6b7a", letterSpacing: ".4px", margin: "6px 0" }}>LIVE MACHINE DETAIL</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 24px", fontSize: 12 }}>
            {liveRows.map((r) => (
              <div key={r.k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                <span style={{ color: "#8a94a3" }}>{r.k}</span><span style={{ color: "#1f2733" }}>{r.v}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#8a94a3", marginTop: 6 }}>{detailNote}</div>
        </div>

        <div style={{ margin: "8px 18px 4px", padding: "10px 14px", background: "rgba(250,238,218,.55)", border: "1px solid rgba(186,117,23,.3)", borderRadius: 10, fontSize: 13, color: "#7a5410" }}>
          Week / month figures are a preview until the collector accumulates history (Phase 1). Idle / off days will surface here as the wasted-capacity signal.
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid rgba(140,150,165,.18)", display: "flex", alignItems: "center", gap: 14, background: "rgba(245,247,250,.5)", fontSize: 12, color: "#5f6b7a" }}>
          <span>Make <b style={{ color: "#1f2733", fontWeight: 500 }}>{m.make}</b></span>
          <span>{m.mtconnect ? "MTConnect" : "Not MTConnect-confirmed"}</span>
          <button style={{ ...btnStyle, marginLeft: "auto" }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, tone, sub }) {
  const toneColor = tone === "amber" ? "#b07207" : "#0b8a63";
  const bg = tone === "amber" ? "rgba(250,238,218,.7)" : "rgba(245,247,250,.85)";
  return (
    <div style={{ background: bg, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontSize: 12, color: tone === "amber" ? "#8a6a2a" : "#8a94a3" }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 500, color: toneColor, marginTop: 2, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: toneColor, marginTop: 5 }}>{sub}</div>
    </div>
  );
}

// ── minimal MTConnect /current parser ────────────────────────────────────────
function parseMTConnect(xmlText) {
  try {
    if (typeof xmlText !== "string") return null;
    const doc = new DOMParser().parseFromString(xmlText, "text/xml");
    if (doc.getElementsByTagName("parsererror").length) return null;
    const txt = (tag, attrMatch) => {
      const els = Array.from(doc.getElementsByTagName(tag));
      for (const el of els) {
        if (!attrMatch || (el.getAttribute("name") || "").toLowerCase().includes(attrMatch)
            || (el.getAttribute("subType") || "").toLowerCase().includes(attrMatch)
            || (el.getAttribute("type") || "").toLowerCase().includes(attrMatch)) {
          return (el.textContent || "").trim();
        }
      }
      return "";
    };
    const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
    const rpm = num(txt("RotaryVelocity")) ?? num(txt("SpindleSpeed")) ?? 0;
    const mode = txt("ControllerMode") || txt("Execution");
    const program = txt("Program");
    const tool = txt("ToolNumber") || txt("ToolId");
    const parts = num(txt("PartCount"));
    const exec = (txt("Execution") || "").toUpperCase();
    const powerOn = num(txt("AccumulatedTime", "powered")) ?? num(txt("AccumulatedTime", "power"));
    const cutting = num(txt("AccumulatedTime", "cutting")) ?? num(txt("AccumulatedTime", "cut"));
    let eff = null;
    if (powerOn && cutting && powerOn > 0) eff = Math.round((cutting / powerOn) * 100);
    let status = "r";
    if (exec.includes("ACTIVE") || rpm > 1) status = "r";
    else if (exec.includes("READY") || exec.includes("STOPPED") || exec.includes("INTERRUPTED")) status = "i";
    else if (exec.includes("UNAVAILABLE")) status = "o";
    return {
      rpm: Math.round(rpm) || 0, mode, program, tool, parts,
      status: status === "r" ? "R" : status,
      eff: eff ?? 72,
    };
  } catch { return null; }
}
